/* eslint-disable indent */
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { OrderProperty } from 'types/order';

import { useCreateLoanModal } from './hooks';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  cancelButton: {
    textTransform: 'none',
    marginRight: theme.spacing(3),
  },
}));

type Props = {
  onClose: () => void;
  properties: OrderProperty[];
};

export const CreateLoanModal: FC<Props> = ({ onClose, properties }) => {
  const classes = useStyles();
  const { createLoanForm, onConfirm, isCreateLoanLoading } = useCreateLoanModal({
    onClose,
    properties
  });

  const { control } = createLoanForm;

  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <form onSubmit={onConfirm}>
        <DialogContent style={{ padding: 24 }}>
          <Grid container direction="column" spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" style={{ color: 'black' }}>
                {t('loan:createLoan')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="loanNumber"
                render={({ field }) => (
                  <TextField
                    label={t('payoffs:loan-number-if-known')}
                    name="loan_number"
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="estateId"
                render={({ field: { value, ...field } }) => (
                  <SelectField
                    {...field}
                    value={value || 'none'}
                    label={t('orders:propertyLabel')}
                    handleBlur={field.onBlur}
                    handleChange={(e) => {
                      const selectedValue = e.target.value === 'none' ? null : e.target.value;
                      field.onChange(selectedValue);
                    }}
                    emptyValue={false}
                    data={[
                      { _id: 'none', description: t('payoffs:none-property') },
                      ...(properties.map((property) => ({
                        ...property,
                        description: property.address.address,
                      })) as any[]),
                    ]}
                    dataKey="_id"
                    dataValue="_id"
                    dataText="description"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" flex="1" justifyContent="flex-end" flexDirection="row">
                <Button
                  className={classes.cancelButton}
                  onClick={onClose}
                  disabled={isCreateLoanLoading}
                >
                  {t('parties:cancel')}
                </Button>
                <BaseButton
                  text={t('common:save')}
                  type="submit"
                  isLoading={isCreateLoanLoading}
                  disabled={!createLoanForm.formState.isValid}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </form>
    </Dialog>
  );
};
